import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, isToday, addMinutes } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Truck, Package, Clock, Banknote, CreditCard, CheckCircle2, X, ChevronLeft, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isAuthenticated = () => {
  return localStorage.getItem('admin_token') === 'authenticated';
};

const KitchenView = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin');
      return;
    }
    fetchOrders();
    const interval = setInterval(fetchOrders, 15000); // refresh every 15s
    const tick = setInterval(() => setNow(Date.now()), 30000); // re-render timers every 30s
    return () => { clearInterval(interval); clearInterval(tick); };
  }, [navigate]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`);
      setOrders(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/orders/${id}/status`, { status });
      fetchOrders();
    } catch (e) {
      console.error(e);
    }
  };

  // Today's orders that are pending or confirmed (active in kitchen)
  const activeOrders = orders.filter((o) => {
    if (!['pending', 'confirmed'].includes(o.status)) return false;
    if (!o.created_at) return true;
    try {
      return isToday(parseISO(o.created_at));
    } catch {
      return true;
    }
  });

  // Compute estimated ready time and minutes left
  const enriched = activeOrders.map((o) => {
    const created = o.created_at ? parseISO(o.created_at) : new Date();
    const delay = o.estimated_delay_minutes;
    const readyAt = delay ? addMinutes(created, delay) : null;
    const minutesLeft = readyAt ? Math.round((readyAt.getTime() - now) / 60000) : null;
    return { ...o, readyAt, minutesLeft };
  });

  // Sort by minutesLeft ascending (most urgent first), pending always on top
  enriched.sort((a, b) => {
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (a.status !== 'pending' && b.status === 'pending') return 1;
    if (a.minutesLeft === null && b.minutesLeft === null) return 0;
    if (a.minutesLeft === null) return 1;
    if (b.minutesLeft === null) return -1;
    return a.minutesLeft - b.minutesLeft;
  });

  const pendingCount = enriched.filter((o) => o.status === 'pending').length;

  // Color of the timer pill based on minutes left
  const timerColor = (m) => {
    if (m === null || m === undefined) return 'bg-stone-700 text-stone-200';
    if (m <= 0) return 'bg-red-600 text-white animate-pulse';
    if (m <= 5) return 'bg-orange-500 text-white';
    if (m <= 15) return 'bg-amber-400 text-stone-900';
    return 'bg-emerald-500 text-white';
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-4 sm:p-6" data-testid="kitchen-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold">🍳 Cuisine — Aujourd'hui</h1>
          <p className="text-stone-400 text-sm mt-1">
            {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })} — Auto-refresh 15s
          </p>
        </div>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <div className="px-4 py-2 rounded-full bg-yellow-500 text-black font-bold text-lg" data-testid="kitchen-pending-count">
              {pendingCount} en attente
            </div>
          )}
          <Button
            onClick={fetchOrders}
            variant="outline"
            size="sm"
            className="bg-transparent border-white/20 text-white hover:bg-white/10"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            onClick={() => navigate('/admin')}
            variant="outline"
            size="sm"
            className="bg-transparent border-white/20 text-white hover:bg-white/10"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Admin
          </Button>
        </div>
      </div>

      {/* Orders Grid */}
      {enriched.length === 0 ? (
        <div className="text-center mt-32 text-stone-500">
          <div className="text-7xl mb-4">😴</div>
          <p className="text-2xl">Aucune commande en cours</p>
          <p className="text-stone-600 mt-2">Profitez de la pause !</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {enriched.map((o) => (
            <div
              key={o.id}
              className={`rounded-2xl p-5 ${
                o.status === 'pending'
                  ? 'bg-yellow-900/40 border-2 border-yellow-500/60'
                  : 'bg-stone-800/80 border border-stone-700'
              }`}
              data-testid={`kitchen-order-${o.id}`}
            >
              {/* Top row */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-2xl font-bold">
                    #{o.id?.slice(-4).toUpperCase()}
                  </span>
                  {o.order_type === 'livraison' ? (
                    <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                      <Truck className="w-3 h-3" /> LIVRAISON
                    </span>
                  ) : (
                    <span className="bg-purple-500 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                      <Package className="w-3 h-3" /> EMPORTER
                    </span>
                  )}
                  {o.payment_method === 'cash' ? (
                    <span className="bg-orange-600 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                      <Banknote className="w-3 h-3" /> Espèces
                    </span>
                  ) : (
                    <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                      <CreditCard className="w-3 h-3" /> Payé
                    </span>
                  )}
                </div>
                {o.minutesLeft !== null && (
                  <span
                    className={`px-3 py-1 rounded-full text-base font-extrabold flex items-center gap-1 ${timerColor(
                      o.minutesLeft
                    )}`}
                  >
                    <Clock className="w-4 h-4" />
                    {o.minutesLeft <= 0 ? 'Maintenant !' : `${o.minutesLeft} min`}
                  </span>
                )}
              </div>

              {/* Customer */}
              <div className="text-lg font-semibold mb-1">{o.customer_name}</div>
              <a
                href={`tel:${o.customer_phone}`}
                className="text-stone-400 text-sm block mb-3 hover:text-white"
              >
                📞 {o.customer_phone}
              </a>

              {/* Delivery address */}
              {o.order_type === 'livraison' && o.delivery_address && (
                <div className="bg-blue-950/60 border border-blue-800/60 rounded-lg p-2 mb-3 text-sm">
                  📍 {o.delivery_address}
                </div>
              )}

              {/* Items */}
              <ul className="space-y-1 mb-4">
                {(o.items || []).map((it, idx) => (
                  <li key={idx} className="text-base flex items-baseline gap-2">
                    <span className="font-bold text-2xl text-yellow-300">{it.quantity}x</span>
                    <span className="flex-1">
                      {it.name}
                      {it.pasta_type && <span className="text-stone-400 text-sm"> ({it.pasta_type})</span>}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Notes */}
              {o.notes && (
                <div className="bg-amber-950/60 border border-amber-700/60 rounded-lg p-2 mb-3 text-sm">
                  📝 <strong>Notes :</strong> {o.notes}
                </div>
              )}

              {/* Total + ready time */}
              <div className="flex items-center justify-between text-sm text-stone-400 mb-3">
                <span>Total : <strong className="text-white">{Number(o.total || 0).toFixed(2)} €</strong></span>
                {o.readyAt && (
                  <span>Prête vers <strong className="text-white">{format(o.readyAt, 'HH:mm')}</strong></span>
                )}
                {!o.readyAt && o.desired_time && (
                  <span>Souhaitée <strong className="text-white">{o.desired_time}</strong></span>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {o.status === 'pending' && (
                  <div className="text-yellow-300 text-sm font-semibold w-full text-center py-2 bg-yellow-900/30 rounded-lg">
                    ⚠️ À confirmer dans /admin
                  </div>
                )}
                {o.status === 'confirmed' && (
                  <Button
                    onClick={() => handleStatus(o.id, 'completed')}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-6 text-base"
                    data-testid={`kitchen-complete-${o.id}`}
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" /> Terminée
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KitchenView;
